import {
  ArrayInput,
  BooleanInput,
  Button,
  maxLength,
  required,
  SimpleFormIterator,
  TextInput,
  useNotify,
  useRefresh,
  useSimpleFormIteratorItem,
} from "react-admin";
import { useWatch } from "react-hook-form";
import CheckIcon from "@mui/icons-material/Check";
import { Grid, Typography } from "@mui/material";

import { settings } from "app";
import { erasmusRequests } from "api/erasmusApi";
import FormModal from "server/components/Modals/FormModal";
import EmployeeInput from "server/components/Fields/EmployeeInput";
import dataProvider from "server/dataProvider";
import { validateInternshipApprovals } from "../utils";

const INTERNSHIPS_SOURCE = "internships";

// One row per internship of the student - the backend rejects a list that does not cover them all,
// so rows cannot be added or removed here.
const InternshipApprovalFields = ({ watchPrefix, name }) => {
  const employeeId = useWatch({ name: `${watchPrefix}.employeeId` });
  const externalMentorName = useWatch({ name: `${watchPrefix}.externalMentorName` });

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
      >
        <Typography color="primary">{name}</Typography>
      </Grid>
      {/* Mentor is a XOR and one of the two is required, so each input disables the other. */}
      <Grid
        item
        xs={12}
        md={6}
      >
        <EmployeeInput
          source="employeeId"
          label="Ментор (преподавател)"
          fullWidth
          disabled={Boolean(externalMentorName?.trim())}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="externalMentorName"
          label="Ментор (външен)"
          fullWidth
          disabled={Boolean(employeeId)}
          validate={maxLength(100)}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        {/* The notification reaches the mentor's own mailbox, so the wording stays neutral. */}
        <BooleanInput
          source="notifyHeadOfDepartment"
          label="Изпрати нотификация по имейл до ментора"
          defaultValue={false}
          disabled={!employeeId}
          sx={{ width: "fit-content" }}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        <ArrayInput
          source="locations"
          label="Локации на практиката"
          defaultValue={[{}]}
        >
          <SimpleFormIterator
            inline={false}
            disableReordering
            sx={settings.style.iteratorWithoutGap}
          >
            <TextInput
              source="location"
              label="Локация"
              fullWidth
              validate={[required(), maxLength(200)]}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
    </Grid>
  );
};

const InternshipApprovalIteratorItem = ({ internships }) => {
  const { index } = useSimpleFormIteratorItem();

  return (
    <InternshipApprovalFields
      watchPrefix={`${INTERNSHIPS_SOURCE}.${index}`}
      name={internships?.[index]?.name}
    />
  );
};

const CoordinatorApproveDialog = ({ id, internships = [] }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const defaultValues = {
    [INTERNSHIPS_SOURCE]: internships.map(internship => ({
      internshipId: internship?.id,
      employeeId: null,
      externalMentorName: null,
      notifyHeadOfDepartment: false,
      locations: [{}],
    })),
  };

  const handleApprove = async data => {
    const payload = {
      internships: data[INTERNSHIPS_SOURCE].map(internship => ({
        internshipId: internship.internshipId,
        employeeId: internship.employeeId ?? null,
        externalMentorName: internship.externalMentorName?.trim() || null,
        notifyHeadOfDepartment: Boolean(internship.employeeId) && Boolean(internship.notifyHeadOfDepartment),
        locations: internship.locations,
      })),
    };

    await dataProvider.customRequest(erasmusRequests.approveIncomingInternshipStudentByCoordinator(id), payload, "PUT");
    notify("Успешно одобрение от координатор", { type: "success" });
    refresh();
  };

  if (!internships.length) return null;

  return (
    <FormModal
      titleContent="Одобрение от координатор"
      dialogSize="md"
      defaultValues={defaultValues}
      onConfirm={handleApprove}
      triggerButton={
        <Button
          label="Одобри"
          variant="contained"
          color="success"
          startIcon={<CheckIcon />}
        />
      }
    >
      <ArrayInput
        source={INTERNSHIPS_SOURCE}
        label={false}
        validate={validateInternshipApprovals}
        fullWidth
      >
        <SimpleFormIterator
          inline={false}
          disableAdd
          disableRemove
          disableReordering
          sx={settings.style.iteratorWithoutGap}
        >
          <InternshipApprovalIteratorItem internships={internships} />
        </SimpleFormIterator>
      </ArrayInput>
    </FormModal>
  );
};

export default CoordinatorApproveDialog;
